/*
 * Vollenweider pickup date engine (storefront).
 * Ported from the microservice api/pickup/dateCalc.js so the theme can compute
 * the earliest pickup date client-side without an app / app proxy.
 *
 * Keep VW_HOLIDAYS in sync with the microservice data/holidays.json ("common"),
 * which is still used by the order-create webhook.
 */
(function () {
  if (window.VWPickup) return;

  var TZ = 'Europe/Zurich';
  var CUTOFF_HOUR = 9;
  var PRODUCTION_TIME_FALLBACK = 3;

  // Swiss holidays (location "other"). Sundays stay selectable, but are not production days.
  var VW_HOLIDAYS = [
    '2026-01-01',
    '2026-01-02',
    '2026-04-06',
    '2026-05-01',
    '2026-05-14',
    '2026-05-25',
    '2026-08-01',
    '2026-12-25',
    '2026-12-26',
    '2027-01-01',
    '2027-01-02'
  ];

  var HOLIDAY_SET = {};
  VW_HOLIDAYS.forEach(function (d) {
    HOLIDAY_SET[d] = true;
  });

  var WEEKDAYS_DE = [
    'Sonntag',
    'Montag',
    'Dienstag',
    'Mittwoch',
    'Donnerstag',
    'Freitag',
    'Samstag'
  ];

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  // Returns the current wall-clock time in Europe/Zurich as { year, month, day, hour }.
  function nowInZurich() {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hour12: false
    }).formatToParts(new Date());

    var map = {};
    parts.forEach(function (p) {
      map[p.type] = p.value;
    });

    return {
      year: parseInt(map.year, 10),
      month: parseInt(map.month, 10),
      day: parseInt(map.day, 10),
      hour: parseInt(map.hour, 10) % 24
    };
  }

  // Work on a UTC-anchored Date (noon) so day iteration is DST-safe.
  function makeUTC(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  function toISO(date) {
    return (
      date.getUTCFullYear() +
      '-' +
      pad(date.getUTCMonth() + 1) +
      '-' +
      pad(date.getUTCDate())
    );
  }

  function addDays(date, days) {
    var d = new Date(date.getTime());
    d.setUTCDate(d.getUTCDate() + days);
    return d;
  }

  // Sundays and holidays are not production days. Sundays remain selectable in the calendar.
  function isNonWorkday(date) {
    return date.getUTCDay() === 0 || HOLIDAY_SET[toISO(date)] === true;
  }

  function isHoliday(date) {
    return HOLIDAY_SET[toISO(date)] === true;
  }

  function isBlocked(iso) {
    var parts = iso.split('-');
    var date = makeUTC(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10),
      parseInt(parts[2], 10)
    );
    return isHoliday(date);
  }

  function addWorkdays(start, days) {
    var current = start;
    var remaining = days;
    while (remaining > 0) {
      current = addDays(current, 1);
      if (!isNonWorkday(current)) remaining--;
    }
    while (isNonWorkday(current)) {
      current = addDays(current, 1);
    }
    return current;
  }

  function parseProductionTime(raw, fallback) {
    var fb = typeof fallback === 'number' ? fallback : PRODUCTION_TIME_FALLBACK;
    if (raw === null || raw === undefined || raw === '') return fb;
    var n = parseInt(String(raw).trim(), 10);
    return isNaN(n) || n < 1 ? fb : n;
  }

  function calcEarliest(productionDays) {
    var now = nowInZurich();
    var today = makeUTC(now.year, now.month, now.day);

    var startDay = now.hour < CUTOFF_HOUR ? today : addDays(today, 1);

    var firstProductionDay = startDay;
    while (isNonWorkday(firstProductionDay)) {
      firstProductionDay = addDays(firstProductionDay, 1);
    }

    return toISO(addWorkdays(firstProductionDay, productionDays));
  }

  function blockedDates() {
    return VW_HOLIDAYS.slice();
  }

  function formatDE(iso) {
    var p = iso.split('-');
    return p[2] + '.' + p[1] + '.' + p[0];
  }

  function isISODate(val) {
    return /^\d{4}-\d{2}-\d{2}$/.test(val);
  }

  function toCartFormat(val) {
    return isISODate(val) ? formatDE(val) : val;
  }

  // Local-time helpers for flatpickr (which works with local Date objects).
  function isoToLocalDate(iso) {
    var p = iso.split('-');
    return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
  }

  function localDateToISO(date) {
    return (
      date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
    );
  }

  function formatLongDE(iso) {
    var parts = iso.split('-');
    var date = makeUTC(
      parseInt(parts[0], 10),
      parseInt(parts[1], 10),
      parseInt(parts[2], 10)
    );
    return WEEKDAYS_DE[date.getUTCDay()] + ', ' + formatDE(iso);
  }

  window.VWPickup = {
    parseProductionTime: parseProductionTime,
    calcEarliest: calcEarliest,
    isBlocked: isBlocked,
    blockedDates: blockedDates,
    formatDE: formatDE,
    formatLongDE: formatLongDE
  };

  function initProduct(container) {
    if (container.getAttribute('data-vw-init') === '1') return;
    container.setAttribute('data-vw-init', '1');

    var loadingEl = container.querySelector('.vw-pickup-loading');
    var resultEl = container.querySelector('.vw-pickup-result');
    var earliestEl = container.querySelector('.vw-earliest');

    var days = parseProductionTime(container.getAttribute('data-production-days'));
    var iso = calcEarliest(days);

    if (loadingEl) loadingEl.hidden = true;

    if (iso && earliestEl && resultEl) {
      earliestEl.textContent = formatLongDE(iso);
      resultEl.hidden = false;
    }
  }

  function initCart(container) {
    if (container.getAttribute('data-vw-init') === '1') return;
    container.setAttribute('data-vw-init', '1');

    var input = container.querySelector('input[type="date"]');
    var hint = container.querySelector('.vw-picker-hint');
    var earliestEl = container.querySelector('.vw-earliest');
    var clearBtn = container.querySelector('.vw-pickup-clear');
    var tooltipBtn = container.querySelector('.vw-pickup-tooltip');
    if (!input) return;

    if (tooltipBtn) {
      tooltipBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        tooltipBtn.classList.toggle('is-open');
      });
      document.addEventListener('click', function (e) {
        if (!tooltipBtn.contains(e.target)) {
          tooltipBtn.classList.remove('is-open');
        }
      });
    }

    var days = parseProductionTime(container.getAttribute('data-production-days'));
    var blocked = blockedDates();
    var earliest = calcEarliest(days);

    if (earliest) {
      input.min = earliest;
      if (earliestEl) {
        // earliestEl.textContent =
        //   'Frühestens abholbereit am: ' + formatDE(earliest) + ', ab 09:00 Uhr';
      }
      setAbholdatum(earliest);
    }

    // Validate a selected value, update the hint, checkout button and cart attributes.
    function handleSelection(val) {
      if (!val) {
        if (hint) hint.textContent = '';
        setCheckoutEnabled(true);
        if (earliest) setAbholdatum(earliest);
        return;
      }
      if (earliest && val < earliest) {
        if (hint) hint.textContent = 'Bitte wähle ein Datum ab ' + formatDE(earliest) + '.';
        setCheckoutEnabled(false);
        return;
      }
      if (isBlocked(val) || blocked.indexOf(val) !== -1) {
        if (hint) {
          hint.textContent =
            'Dieser Tag ist nicht verfügbar (Feiertag). Bitte anderen Tag wählen.';
        }
        setCheckoutEnabled(false);
        return;
      }
      if (hint) hint.textContent = '';
      setCheckoutEnabled(true);
      updateCartAttributes({ Abholdatum: val });
    }

    // Show the "clear" button only when a date is selected.
    function toggleClear(hasValue) {
      if (clearBtn) clearBtn.hidden = !hasValue;
    }

    var fp = null;

    if (typeof window.flatpickr === 'function') {
      // Holidays are greyed out and cannot be selected. Sundays are allowed.
      // No altInput so flatpickr enhances the existing component input in place.
      var disable = blocked.map(function (iso) {
        return isoToLocalDate(iso);
      });

      fp = window.flatpickr(input, {
        minDate: earliest ? isoToLocalDate(earliest) : null,
        dateFormat: 'd.m.Y',
        allowInput: false,
        locale:
          window.flatpickr.l10ns && window.flatpickr.l10ns.de ? 'de' : 'default',
        disable: disable,
        onChange: function (selectedDates) {
          var iso = selectedDates.length ? localDateToISO(selectedDates[0]) : '';
          handleSelection(iso);
          toggleClear(selectedDates.length > 0);
        }
      });
    } else {
      // Fallback: plain native date input with manual validation.
      input.addEventListener('change', function () {
        handleSelection(this.value);
        toggleClear(!!this.value);
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (fp) {
          // clear() fires onChange, which resets and hides the button.
          fp.clear();
        } else {
          input.value = '';
          handleSelection('');
          toggleClear(false);
        }
      });
    }

    function setAbholdatum(dateVal) {
      updateCartAttributes({ Abholdatum: dateVal });
    }
  }

  function updateCartAttributes(attributes) {
    var formatted = {};
    for (var key in attributes) {
      if (Object.prototype.hasOwnProperty.call(attributes, key)) {
        formatted[key] = toCartFormat(attributes[key]);
      }
    }
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: formatted })
    }).catch(function () {});
  }

  // Disable the cart checkout button while an invalid pickup date is selected.
  function setCheckoutEnabled(enabled) {
    var checkout = document.getElementById('checkout');
    if (!checkout) return;
    checkout.disabled = !enabled;
    checkout.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  }

  function initAll() {
    var products = document.querySelectorAll('.vw-pickup-product');
    for (var i = 0; i < products.length; i++) initProduct(products[i]);

    var carts = document.querySelectorAll('.vw-pickup-cart');
    for (var j = 0; j < carts.length; j++) initCart(carts[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
